import { useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans, useTranslation } from 'react-i18next';
import { Button, SubmitButton } from 'components';
import {
  Feature,
  Offer,
  OfferProduct,
  Product,
  ProductType,
  useCountryByPriceGroups,
  useCountryPriceTiers,
} from 'queries';
import { getGroupProducts, getRelevantProducts } from 'lib/planAndOffer';
import { compareProducts } from 'lib/products';
import { SubTotal, ProductListSection, MinMaxTotal } from 'components';
import {
  sumProducts,
  NO_PRICE_GROUP,
  computeIncentive,
  toProductArr,
} from 'lib/priceComputation';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'stretch',

    borderRadius: theme.borderRadius.std,
    boxShadow: theme.boxShadow.base,
    marginBottom: theme.marginBase * 2,

    [theme.media.tablet]: {
      flexDirection: 'column',
    },
  },
  detailsBlock: {
    backgroundColor: theme.colors.grayBackground,
    flex: 2,
    padding: theme.marginBase * 4,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
    justifyContent: 'center',
  },
  totalBlock: {
    backgroundColor: theme.colors.white,
    padding: theme.marginBase * 5,
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
    [theme.media.tablet]: {
      textAlign: 'center',
    },
  },
  totalTitle: {
    ...theme.fonts.h5,
    textTransform: 'uppercase',
  },
  total: {
    ...theme.fonts.h1,
  },
  totalAmount: {
    ...theme.fonts.body,
  },
  totalTaxes: {
    marginBottom: theme.marginBase * 2,
  },
  totalButtons: {
    display: 'flex',
    flexDirection: 'column',
  },
  spacer: {
    width: '100%',
    height: 0,
    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.grayBorder,
    },
  },
  strong: {
    ...theme.fonts.h1,
  },
  saveQuoteButton: {
    marginTop: theme.marginBase,
  },
}));

interface Props {
  numberOfParticipants: number;
  onContinueClick?: () => any;
  offers: Offer[];
  countryKeys?: string[];
  duration: number;
  onSaveQuoteClick?: () => any;
  isQuoteSaving?: boolean;
  shouldApplyVat?: boolean;
}

export const TotalBlock = ({
  duration,
  numberOfParticipants,
  countryKeys,
  onContinueClick,
  onSaveQuoteClick,
  isQuoteSaving,
  offers,
  shouldApplyVat = false,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t, i18n } = useTranslation();

  const countryByPriceGroups = useCountryByPriceGroups(countryKeys);
  const countryPriceGroupIds = useCountryPriceTiers(countryKeys);

  const offerProducts = useMemo(() => {
    let offerProducts: OfferProduct[] = [];
    for (const offer of offers) {
      const ops = getRelevantProducts(
        offer.products,
        {
          countryPriceGroupIds,
        },
        offerProducts,
      );
      offerProducts = offerProducts.concat(ops);
    }
    return offerProducts
      .filter((p) => p.product?.title)
      .sort((a, b) => compareProducts(a.product!, b.product!));
  }, [offers, i18n, countryPriceGroupIds]);

  const components = useMemo(
    () => ({
      strong: <strong className={classes.strong} />,
      h2: <strong className={classes.total} />,
    }),
    [classes],
  );

  const nonEmptyCountryPriceGroups = useMemo(
    (): string[] =>
      countryPriceGroupIds?.length ? countryPriceGroupIds : [NO_PRICE_GROUP],
    [countryPriceGroupIds],
  );

  const globalProducts = useMemo(() => {
    return offerProducts
      .filter(
        (op) =>
          op.product?.type &&
          op.product?.feature !== Feature.SEND_INVOICE &&
          ![ProductType.PARTICIPANT_FEE, ProductType.INCENTIVE].includes(
            op.product?.type,
          ),
      )
      .map(({ product }) => product) as Product[];
  }, [offerProducts]);

  const sendInvoiceProduct = useMemo(
    () =>
      offerProducts.find((op) => op.product?.feature === Feature.SEND_INVOICE)
        ?.product,
    [offerProducts],
  );

  const productsByGroup = useMemo(() => {
    const res: Record<string, OfferProduct[]> = {};
    for (const group of nonEmptyCountryPriceGroups) {
      res[group] = getGroupProducts(offerProducts, group);
    }
    return res;
  }, [nonEmptyCountryPriceGroups, offerProducts]);

  const totals = useMemo(() => {
    return nonEmptyCountryPriceGroups.map((group) => {
      const products = productsByGroup[group];
      const incentive = computeIncentive(products, duration);
      return sumProducts(products, numberOfParticipants, duration, incentive);
    });
  }, [nonEmptyCountryPriceGroups, productsByGroup]);

  const totalAmount = Math.max(...totals);
  const totalWithTaxes = shouldApplyVat ? totalAmount * 1.2 : totalAmount;

  const severalCountries = nonEmptyCountryPriceGroups.length > 1;
  const hasGroupWithMultipleCountries = Object.keys(countryByPriceGroups).some(
    (groupId) => countryByPriceGroups[groupId]?.length >= 2,
  );

  return (
    <div className={classes.container}>
      <div className={classes.detailsBlock}>
        {nonEmptyCountryPriceGroups.map((group) => (
          <ProductListSection
            products={toProductArr(productsByGroup[group]).filter((p) =>
              [ProductType.PARTICIPANT_FEE, ProductType.INCENTIVE].includes(
                p.type,
              ),
            )}
            duration={duration}
            title={t('studySetup.generalSetup.cost.recruitment')}
            countryKeys={
              severalCountries ? countryByPriceGroups[group] : undefined
            }
            numberOfParticipants={
              !severalCountries ? numberOfParticipants : undefined
            }
            displayNumberOfParticipant={!severalCountries}
            displayCountriesInSeparateRow={hasGroupWithMultipleCountries}
          />
        ))}

        {!!globalProducts?.length && (
          <ProductListSection
            products={globalProducts}
            numberOfParticipants={numberOfParticipants}
            duration={duration}
            title={t('studySetup.generalSetup.cost.support')}
          />
        )}

        {!!sendInvoiceProduct && (
          <ProductListSection
            product={sendInvoiceProduct}
            numberOfParticipants={numberOfParticipants}
            duration={duration}
          />
        )}

        {!severalCountries && shouldApplyVat && (
          <SubTotal totalExclTaxes={totalAmount} taxes={totalAmount * 0.2} />
        )}
        {severalCountries && (
          <>
            <hr className={classes.spacer} />
            <MinMaxTotal
              prices={totals}
              shouldApplyVat={shouldApplyVat}
              numberOfParticipants={numberOfParticipants}
            />
          </>
        )}
      </div>

      <div className={classes.totalBlock}>
        <div className={classes.totalTitle}>
          <Trans i18nKey="studySetup.generalSetup.cost.total.title" />
        </div>

        <div className={classes.totalAmount}>
          <Trans
            i18nKey="studySetup.generalSetup.cost.total.taxes"
            values={{
              count: totalWithTaxes,
            }}
            components={components}
          />
        </div>

        <div className={classes.totalButtons}>
          <SubmitButton
            text={t('studySetup.generalSetup.cost.continue')}
            withNextArrow
            onClick={onContinueClick}
            small
            color="accent"
            disabled={isQuoteSaving}
            trackingName="Accept Price"
            id="accept_price_button"
          />
          {onSaveQuoteClick && (
            <Button
              text={t('studySetup.generalSetup.cost.saveQuote')}
              onClick={onSaveQuoteClick}
              small
              variant="transparent"
              disabled={isQuoteSaving}
              isLoading={isQuoteSaving}
              className={classes.saveQuoteButton}
              trackingName="Save Quote"
              id="save_quote_button"
              color="accent"
            />
          )}
        </div>
      </div>
    </div>
  );
};
